import { createApi } from "@reduxjs/toolkit/dist/query/react"
import {
  HelocWorkflowConfig,
  HelocWorkflowConfigUpsertPayload,
  HelocWorkflowConfigsListResponse
} from "../../types"
import {
  HELOC_WORKFLOW_CONFIGS_ENDPOINT,
  HELOC_WORKFLOW_CONFIG_ENDPOINT,
  RTK_SLICE_API_LIST
} from "../../common/constants"
import bbApi from "../../api"
import { baseQuery, getTags } from "../api"
import { getQueryString } from "../../common/utils"

const reducerPath = "heloc-workflow"

export const helocWorkflowApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [reducerPath],
  endpoints: builder => ({
    findHelocWorkflows: builder.query({
      query: (brand_id: string) =>
        `${HELOC_WORKFLOW_CONFIGS_ENDPOINT}?brand_id=${brand_id}`,
      transformResponse: (responseData: HelocWorkflowConfigsListResponse) =>
        responseData?.heloc_workflow_configs ?? [],
      providesTags: result => getTags(reducerPath, "id", result)
    }),
    fetchHelocWorkflowDetails: builder.query({
      query: (params: { id: string }) =>
        `${HELOC_WORKFLOW_CONFIG_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: HelocWorkflowConfig) => responseData,
      providesTags: (result, error, arg) => [{ type: reducerPath, id: arg.id }]
    }),
    /**
     * No id resolves to the built-in wizard (`id: 0`) — the starting point a
     * new config is seeded from.
     */
    fetchDefaultHelocWorkflowDetails: builder.query({
      query: () => `${HELOC_WORKFLOW_CONFIG_ENDPOINT}?id=`,
      transformResponse: (responseData: HelocWorkflowConfig) => responseData
    }),
    updateOrCreateHelocWorkflow: builder.mutation({
      queryFn: async (payload: HelocWorkflowConfigUpsertPayload) => {
        const { id, ...rest } = payload
        // An empty/zero id means CREATE — id 0 is the built-in fallback, never
        // a row that can be updated.
        const body: HelocWorkflowConfigUpsertPayload = Number(id)
          ? { ...rest, id: Number(id) }
          : rest
        const data = await bbApi.helocWorkflow.updateOrCreate(body)
        return { data }
      },
      invalidatesTags: (result, error, arg) => {
        const resultData = result as
          | { status?: string; message?: string }
          | undefined
        // Validation rejections come back HTTP 200 with status "error" — don't
        // invalidate, the stored config hasn't changed.
        if (error || resultData?.status === "error") {
          return []
        }
        return [
          { type: reducerPath, id: arg.id },
          { type: reducerPath, id: RTK_SLICE_API_LIST }
        ]
      }
    }),
    deleteHelocWorkflow: builder.mutation({
      queryFn: async (id: string | number) => {
        const data = await bbApi.helocWorkflow.delete(id)
        return { data }
      },
      invalidatesTags: () => [{ type: reducerPath, id: RTK_SLICE_API_LIST }]
    })
  })
})

export const {
  useFindHelocWorkflowsQuery,
  useFetchHelocWorkflowDetailsQuery,
  useFetchDefaultHelocWorkflowDetailsQuery,
  useUpdateOrCreateHelocWorkflowMutation,
  useDeleteHelocWorkflowMutation
} = helocWorkflowApi
